// Audit trail viewer, ported from renderAuditView (source lines 8109-8238).
// Filtering/pagination now happen server-side (GET /audit?...) instead of
// client-side over the full in-memory array. User/scope filters are plain
// text inputs rather than dropdowns populated from distinct values - the
// source computed those client-side over the whole array, which the
// paginated API doesn't expose; a disclosed simplification.
import { auditApi, getToken } from "./api-client.js";
import { escapeHtml, fmtDate, $ } from "./dom.js";

const PAGE_SIZE = 500;
let page = 1;
let filters = { user: "", scope: "", from: "", to: "", q: "" };

export async function renderAuditView(root: HTMLElement): Promise<void> {
  root.innerHTML = `<div class="loading">Chargement…</div>`;
  const { entries, total } = await auditApi.list({ ...filters, page, pageSize: PAGE_SIZE });
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const rows = entries.length
    ? entries
        .map(
          (e: any) => `<tr>
        <td>${fmtDate(e.timestamp)}</td><td>${escapeHtml(e.user || "")}</td><td>${escapeHtml(e.userRole || "")}</td>
        <td>${escapeHtml(e.action || "")}</td><td>${escapeHtml(e.scope || "")}</td><td>${escapeHtml(e.target || "")}</td>
        <td>${escapeHtml(JSON.stringify(e.oldValue ?? ""))}</td><td>${escapeHtml(JSON.stringify(e.newValue ?? ""))}</td>
        <td>${escapeHtml(e.details || "")}</td>
      </tr>`
        )
        .join("")
    : `<tr><td colspan="9" class="muted">Aucun événement correspondant aux filtres.</td></tr>`;

  const pager =
    totalPages > 1
      ? `<div style="display:flex;gap:8px;align-items:center;justify-content:center;margin:14px 0;padding:10px;background:#f8fafc;border-radius:6px">
      <button class="btn btn-sm" id="auditFirst" ${page <= 1 ? "disabled" : ""}>« Première</button>
      <button class="btn btn-sm" id="auditPrev" ${page <= 1 ? "disabled" : ""}>‹ Précédente</button>
      <span style="margin:0 14px;font-size:13px">Page <strong>${page}</strong> / ${totalPages} — ${total.toLocaleString("fr-FR")} entrées</span>
      <button class="btn btn-sm" id="auditNext" ${page >= totalPages ? "disabled" : ""}>Suivante ›</button>
      <button class="btn btn-sm" id="auditLast" ${page >= totalPages ? "disabled" : ""}>Dernière »</button>
    </div>`
      : "";

  root.innerHTML = `
    <div class="view-header"><h2>📜 Audit trail</h2>
      <p class="muted">${total.toLocaleString("fr-FR")} entrée(s) — toutes persistées (conformité 21 CFR Part 11), exportables en CSV.</p>
    </div>
    <div class="filters-bar">
      <input type="text" id="auditUser" value="${escapeHtml(filters.user)}" placeholder="Utilisateur">
      <input type="text" id="auditScope" value="${escapeHtml(filters.scope)}" placeholder="Scope">
      <input type="date" id="auditFrom" value="${filters.from}">
      <input type="date" id="auditTo" value="${filters.to}">
      <input type="text" id="auditText" value="${escapeHtml(filters.q)}" placeholder="Recherche texte" style="min-width:220px">
      <a class="btn" id="auditExport" href="#">Export CSV</a>
    </div>
    ${pager}
    <table class="data-table audit-table">
      <thead><tr><th>Horodatage</th><th>Utilisateur</th><th>Rôle</th><th>Action</th><th>Scope</th><th>Cible</th><th>Avant</th><th>Après</th><th>Détails</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    ${pager}`;

  const rerender = () => void renderAuditView(root);
  $("#auditUser")!.addEventListener("change", (e) => { filters.user = (e.target as HTMLInputElement).value; page = 1; rerender(); });
  $("#auditScope")!.addEventListener("change", (e) => { filters.scope = (e.target as HTMLInputElement).value; page = 1; rerender(); });
  $("#auditFrom")!.addEventListener("change", (e) => { filters.from = (e.target as HTMLInputElement).value; page = 1; rerender(); });
  $("#auditTo")!.addEventListener("change", (e) => { filters.to = (e.target as HTMLInputElement).value; page = 1; rerender(); });
  let debounce: ReturnType<typeof setTimeout>;
  $("#auditText")!.addEventListener("input", (e) => {
    clearTimeout(debounce);
    const val = (e.target as HTMLInputElement).value;
    debounce = setTimeout(() => { filters.q = val; page = 1; rerender(); }, 300);
  });
  $("#auditFirst")?.addEventListener("click", () => { page = 1; rerender(); });
  $("#auditPrev")?.addEventListener("click", () => { page = Math.max(1, page - 1); rerender(); });
  $("#auditNext")?.addEventListener("click", () => { page = Math.min(totalPages, page + 1); rerender(); });
  $("#auditLast")?.addEventListener("click", () => { page = totalPages; rerender(); });
  $("#auditExport")!.addEventListener("click", async (e) => {
    e.preventDefault();
    const url = auditApi.exportCsvUrl(filters);
    const res = await fetch(url, { headers: { authorization: `Bearer ${getToken()}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "audit_log.csv";
    a.click();
  });
}
